import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useRouter } from 'next/router';

import crossTabTokenRefreshService from 'services/crossTabTokenRefreshServiceSingleton';
import { setAuth0TokenGetter } from 'services/auth0TokenGetter';
import { AuthContext } from './AuthContext';
import LoadingPage from '../../../components/layout/Page/LoadingPage';
import { useNotification } from '../hooks';
import routes from 'routes';
import { ADMIN_PORTAL_ROLES } from 'constants/roles';
import { NO_ADMIN_PORTAL_ACCESS_COOKIE } from 'modules/shared/constants/auth';
import { AUTH0_MFA_ENABLED_CLAIM, getAccessTokenLegacyId, getAccessTokenPermissions } from 'utils/auth0';
import { AUTHENTICATION_ACTIONS, telemetry } from 'utils/telemetry';
import CookieUtils from 'utils/cookies';

import type { AuthContextType } from './AuthContext';

type Auth0UserProfile = {
  'https://springhealth.com/is_mfa_enabled'?: boolean;
  email?: string;
  email_verified?: boolean;
  family_name?: string;
  given_name?: string;
  name?: string;
  sub?: string;
  updated_at?: string;
};

type AuthContextUser = NonNullable<AuthContextType['user']>;

// Pages that render for a signed-in Auth0 user who has no AuthContext user: /logout while it signs
// them out, and the Compass deprecation page for Compass users with no Admin Portal role.
const PATHS_WITHOUT_AUTH_CONTEXT_USER = [routes.Logout.as, routes.CompassRedirectDeprecation.as];

// `id` is the Rotom user ID from the access token, never the email: callers send it to Rotom as
// a user ID, and LaunchDarkly and Mixpanel key the user on it.
const mapAuth0UserToAuthContextUser = (auth0User: Auth0UserProfile, id: string, roles: string[]): AuthContextUser => {
  return {
    id,
    email: auth0User.email ?? '',
    created_at: null,
    confirmed: auth0User.email_verified ?? true,
    first_name: auth0User.given_name ?? auth0User.name ?? auth0User.email ?? '',
    last_name: auth0User.family_name ?? null,
    mfa_enabled: auth0User[AUTH0_MFA_ENABLED_CLAIM] ?? false,
    scopes: roles,
  };
};

/**
 * Auth0AuthContextProvider manages user authentication state when Admin Portal signs in with Auth0.
 * It fetches user identifiers on mount, handles loading states, and provides methods for
 * initializing user data on sign-in and logging out.
 *
 * Re-routing needs should be handled in the layout components.
 *
 * @param {React.ReactNode} children - The child components to be wrapped by the provider.
 * @param {string} [postSignInPath] - Where to send the user once a Universal Login sign-in is hydrated.
 * @returns {JSX.Element} The AuthContext.Provider component with user state and methods.
 */
export const Auth0AuthContextProvider = ({
  children,
  postSignInPath,
}: React.PropsWithChildren<{ postSignInPath?: string }>) => {
  const { asPath, replace } = useRouter();
  const notify = useNotification();
  const timerId = useRef<NodeJS.Timeout | null>(null);
  const {
    error: auth0Error,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading: auth0Loading,
    user: auth0User,
  } = useAuth0();

  const [user, setUser] = useState<AuthContextType['user']>(undefined);
  const apollo = useApolloClient();
  const isHydratingAuthenticatedUser = !PATHS_WITHOUT_AUTH_CONTEXT_USER.includes(asPath) && isAuthenticated && !user;
  // An Auth0 error must not hold the loading screen: children render without a user, so the
  // layouts can route to sign-in and /logout can mount while the error branch below logs out.
  const isAuthContextLoading = auth0Loading || isHydratingAuthenticatedUser;

  const getIdentifiers = useCallback(async () => {
    if (auth0Loading) {
      return;
    }

    if (auth0Error) {
      telemetry.addError(auth0Error);
      notify({
        status: 'error',
        description: 'Could not fetch user data. Please login again after we log you out.',
      });

      timerId.current = setTimeout(() => {
        telemetry.addAction(AUTHENTICATION_ACTIONS.AUTH_CONTEXT_REDIRECT_TO_LOGOUT);

        replace(routes.Logout.as);
      }, 5000);

      return;
    }

    try {
      /**
       * Auth0 is now the source of truth for the signed-in user. We still hydrate the internal
       * AuthContext state here so the rest of the app can keep consuming `user` the same way.
       * The logout route remains the only exception because we do not need to rehydrate a user
       * while we are logging them out.
       */
      if (asPath !== routes.Logout.as && isAuthenticated && auth0User) {
        // Failing to get a token (thrown or empty) is handled by the catch below, which logs out.
        const accessToken = await getAccessTokenSilently();

        if (!accessToken) {
          throw new Error('Could not fetch Auth0 access token.');
        }

        const roles = getAccessTokenPermissions(accessToken);

        if (!ADMIN_PORTAL_ROLES.some((role) => roles.includes(role))) {
          // As the Diglet sign-in does, send Compass users to the Compass deprecation page and anyone
          // else back to sign-in, where the cookie explains why. No user is set, so no Admin Portal
          // page renders; /logout ends the Auth0 session.
          CookieUtils.clearHeaders();

          if (roles.some((role) => role.includes('compass'))) {
            replace(routes.CompassRedirectDeprecation.as);
          } else {
            CookieUtils.set(NO_ADMIN_PORTAL_ACCESS_COOKIE, true, { maxAge: 5 * 60, sameSite: 'lax' });
            replace(routes.Logout.as);
          }

          return;
        }

        const legacyId = getAccessTokenLegacyId(accessToken);

        if (!legacyId) {
          throw new Error('Auth0 access token has no legacy_id claim.');
        }

        const mappedUser = mapAuth0UserToAuthContextUser(auth0User, legacyId, roles);

        // Drop any Diglet session left in the cookies, including its refresh token and Compass
        // handoff cookies: a 401 refreshes through the Auth0 SDK (see the getter registered below).
        CookieUtils.clearHeaders();
        CookieUtils.saveAuth0Headers(
          { access_token: accessToken, scopes: mappedUser.scopes },
          mappedUser.email || mappedUser.id
        );

        if (postSignInPath) {
          // Navigate while the loading screen still covers the page, and after saving the token so
          // the middleware lets a private page through. Setting the user first would render the
          // page Auth0 returned to, whose own redirects would race this one.
          await replace(postSignInPath).catch((error) => telemetry.addError(error));
        }

        setUser(mappedUser);
      } else {
        setUser(undefined);
      }
    } catch (e) {
      telemetry.addError(e);
      notify({
        status: 'error',
        description: 'Could not fetch user data. Please login again after we log you out.',
      });

      timerId.current = setTimeout(() => {
        telemetry.addAction(AUTHENTICATION_ACTIONS.AUTH_CONTEXT_REDIRECT_TO_LOGOUT);

        replace(routes.Logout.as);
      }, 5000);
    }
  }, [
    asPath,
    auth0Error,
    auth0Loading,
    auth0User,
    getAccessTokenSilently,
    isAuthenticated,
    notify,
    postSignInPath,
    replace,
  ]);

  useEffect(() => {
    telemetry.addAction(AUTHENTICATION_ACTIONS.SESSION_BEGIN);

    return () => {
      if (timerId?.current) {
        clearTimeout(timerId.current);
      }
    };
  }, []);

  useEffect(() => {
    // Lets Apollo and fetchHandler get a new Auth0 access token when the API answers 401.
    setAuth0TokenGetter((options) => getAccessTokenSilently(options));

    return () => setAuth0TokenGetter(null);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    getIdentifiers();
    // This effect is intentionally scoped to auth state transitions only: signing in, and an Auth0
    // error (e.g. a callback with ?error=access_denied), which arrives after loading finishes without
    // changing isAuthenticated. Including `getIdentifiers` would cause reruns whenever callback inputs
    // change during hydration, which can repeatedly rehydrate user state leading to infinite refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, auth0Error]);

  const onSignInInitializeUser = (user) => {
    return setUser(user);
  };

  const onLogout = () => {
    apollo.clearStore();
    setUser(undefined);
    crossTabTokenRefreshService.destroy();
  };

  if (isAuthContextLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      <AuthContext.Provider
        value={{ user, loading: isAuthContextLoading, isAuth0Login: true, onSignInInitializeUser, onLogout }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
};
